/**
 * TerraMatrix Universal Data Platform - SaveTemplateModal Component
 *
 * Modal dialog for saving a natural language query as a reusable template.
 * Shows the detected variables and allows naming the template.
 *
 * Phase 5: Data Page UI Integration
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { dataApi, TemplateQuery, TemplateVariable } from '@/services/api/dataApi';
import type { QueryTemplate } from '@/services/api/queryTemplateApi';

interface SaveTemplateModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The natural language query to save as a template (legacy mode) */
  query?: string;
  /** Schema ID for the query (legacy mode) */
  schemaId?: string;
  /** Group ID for the query (legacy mode) */
  groupId?: string;
  /** Pre-extracted template query (new mode) */
  templateQuery?: TemplateQuery;
  /** Pre-extracted template variables (new mode) */
  templateVariables?: Record<string, TemplateVariable>;
  /** Callback when template is saved successfully */
  onSaved?: (template: QueryTemplate) => void;
}

export const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  open,
  onClose,
  query,
  schemaId,
  groupId,
  templateQuery,
  templateVariables,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if we're using the new template mode
  const isTemplateMode = !!templateQuery;

  const handleSave = async () => {
    // Validate name is required for new template mode
    if (isTemplateMode && !name.trim()) {
      setError('Template name is required');
      return;
    }

    // Legacy mode validation
    if (!isTemplateMode && (!schemaId || !groupId)) {
      setError('Schema ID and Group ID are required. Please select a data source first.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let result;

      if (isTemplateMode && templateQuery) {
        // New mode: Use pre-extracted template query
        result = await dataApi.saveNLQueryAsTemplate({
          name: name.trim(),
          description: description.trim() || undefined,
          query: templateQuery,
          variables: templateVariables || {},
        });
      } else {
        // Legacy mode: Send query string to backend for extraction
        result = await dataApi.saveAsTemplate({
          query: query || '',
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          schemaId: schemaId!,
          groupId: groupId!,
        });
      }

      onSaved?.(result.template);
      onClose();

      // Reset form
      setName('');
      setDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setName('');
      setDescription('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save as Template</DialogTitle>
          <DialogDescription>
            Save this query as a reusable template with configurable variables.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-2 space-y-4">
          {/* Original Query Preview - only show in legacy mode */}
          {!isTemplateMode && query && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Query</label>
              <div className="p-3 rounded-lg bg-muted/50 text-sm border border-border">
                <p className="line-clamp-3">{query}</p>
              </div>
            </div>
          )}

          {/* Template Info - show in template mode */}
          {isTemplateMode && templateQuery && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Template Query</label>
              <div className="p-3 rounded-lg bg-muted/50 text-sm border border-border">
                <p className="text-xs text-muted-foreground mb-1">
                  {templateQuery.measures?.length || 0} measure(s)
                  {templateQuery.groupBy?.length ? `, grouped by ${templateQuery.groupBy.length} field(s)` : ''}
                  {templateQuery.timeRange?.preset ? `, time range: ${templateQuery.timeRange.preset}` : ''}
                </p>
                {templateVariables && Object.keys(templateVariables).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs font-medium text-foreground/70 mb-1">Variables:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(templateVariables).map(([varName, varDef]) => (
                        <span
                          key={varName}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary/10 text-primary"
                        >
                          {`{{${varName}}}`}
                          {varDef.default && <span className="ml-1 text-muted-foreground">= {String(varDef.default)}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Template Name
              {!isTemplateMode && <span className="text-muted-foreground font-normal ml-1">(optional)</span>}
              {isTemplateMode && <span className="text-destructive font-normal ml-1">*</span>}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isTemplateMode ? "Enter a name for this template" : "Auto-generated from query if empty"}
              disabled={isSaving}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                         disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Description
              <span className="text-muted-foreground font-normal ml-1">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this template does..."
              rows={2}
              disabled={isSaving}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                         disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
          </div>

          {/* Info about template */}
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                {isTemplateMode ? (
                  <>
                    <p className="font-medium">Template ready to save</p>
                    <p className="text-blue-600 dark:text-blue-300 mt-1">
                      The query and its variables have been extracted. You can use this template
                      in charts and dashboards.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Variables will be detected automatically</p>
                    <p className="text-blue-600 dark:text-blue-300 mt-1">
                      Time ranges, granularity, and other patterns in your query will become
                      configurable variables in the template.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border
                       hover:bg-muted transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium rounded-lg
                       bg-primary text-primary-foreground
                       hover:bg-primary/90 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
                Save Template
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTemplateModal;
